import { FC, useEffect, useState, useCallback } from 'react';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useMediaQuery } from '@mui/material';

import lazyHydrate from 'next-lazy-hydrate';
import { useQuery } from 'react-query';

import { usePagePathParams } from '@hooks_common/usePagePathParams';
import { useWindowSize } from '@hooks_common/useWindowSize';

import { MajorCityWard } from '@hooks_iqfu/public/useAreaData';
import { CompanyMapBounds, getCompaniesList } from '@hooks_iqfu/public/useCompanyData';
import { getSaleCasesList } from '@hooks_iqfu/public/useSaleCase';

import { getCompaniesPageMetaInfo } from '@utils_iqfu/public/getCompaniesPageMetaInfo';
import { isInCompanyPropertyTypePage } from '@utils_iqfu/public/isInCompanyPropertyTypePage';

import BreadCrumbs from '@components_iqfu/public/bread_crumbs/BreadCrumbs';
import CompaniesPriceGeneralInfo from '@components_iqfu/public/companies_price_general_info/CompaniesPriceGeneralInfo';
//※CTAは該当不動産会社のないページでファーストビューになるので、lazy-hydrateしない
import PrimaryCTA from '@components_iqfu/public/cta/PrimaryCTA';
import SecondaryCTA from '@components_iqfu/public/cta/SecondaryCTA';
import ExternalLink from '@components_iqfu/public/external_link/ExternalLink';
import FloatingCV from '@components_iqfu/public/floating_cv/floating_cv';
import IkukoSectionTitleWrapper from '@components_iqfu/public/ikuko_section_title_wrapper/IkukoSectionTitleWrapper';
import MajorCityWardsLinkList from '@components_iqfu/public/major_city_wards_link_list/MajorCityWardsLinkList';
//※該当不動産会社のないページでファーストビューになるので、lazy-hydrateしない
import PageMainLead from '@components_iqfu/public/page_main_lead/PageMainLead';
import PublicPageContainer from '@components_iqfu/public/public_page_container/PublicPageContainer';
import RankingShortColumn from '@components_iqfu/public/ranking_short_column/RankingShortColumn';
import RenderOnScroll from '@components_iqfu/public/render_on_scroll/RenderOnScroll';
import SectionLead from '@components_iqfu/public/section_lead/SectionLead';
import SectionLeadLeftAlign from '@components_iqfu/public/section_lead_left_align/SectionLeadLeftAlign';

import { CityInfo } from '@type-def_iqfu/public/CityDetailData';
import { CompanyListItem } from '@type-def_iqfu/public/CompanyListItem';
import { CityGroups, MarketData, SalesStatsData } from '@type-def_iqfu/public/MarketData';
import { MediaContentArticlesData } from '@type-def_iqfu/public/MediaContentArticlesData';
import { SaleCaseItem } from '@type-def_iqfu/public/SaleCaseItem';

import { MediaQuery } from '@constants_common';
import { CompaniesPageCategory, showPrefectureNameInTitleFlag } from '@constants_iqfu';
import styles from '@styles_iqfu/public/companies.module.scss';
import { CompaniesPageParameter } from 'src/iqfu/data/companiesPagesData';

//※該当不動産会社のないページでファーストビューになるので、lazy-hydrateしない
import CompaniesCitiesCompanyRanking from './company_ranking/CompanyRanking';
import InternalLinksCommon from './internal_links_common/InternalLinksCommon';

const CompanyLocationsMap = dynamic(() => import('@components_iqfu/public/mapbox/CompanyLocationsMap'), {
  //ブラウザでしか動かないのでSSRしない
  ssr: false,
});

const SaleCaseArea = lazyHydrate(() => import('@components_iqfu/public/sale_case_area/SaleCaseArea'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const SearchCompaniesWrapper = lazyHydrate(
  () => import('@components_iqfu/public/search_companies_wrapper/SearchCompaniesWrapper'),
  {
    on: [
      ['scroll', () => document],
      ['delay', 500],
    ],
    compatibleMode: true,
  },
);

const StrengthBasedCompanyRankingLinks = lazyHydrate(
  () => import('@components_iqfu/public/strength_based_company_ranking_links/StrengthBasedCompanyRankingsLinks'),
  {
    on: [
      ['scroll', () => document],
      ['delay', 500],
    ],
    compatibleMode: true,
  },
);

const CheckPrice = lazyHydrate(() => import('@components_iqfu/public/check_price/CheckPrice'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const SearchLocalCompanies = lazyHydrate(
  () => import('@components_iqfu/public/search_local_companies/SearchLocalCompanies'),
  {
    on: [
      ['scroll', () => document],
      ['delay', 500],
    ],
    compatibleMode: true,
  },
);

const PrefecturesLinkList = lazyHydrate(() => import('@components_iqfu/public/prefectures_list/PrefecturesLinkList'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const PricePageLinks = lazyHydrate(() => import('@components_iqfu/public/price_page_links/PricePageLinks'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const CitiesLinkList = lazyHydrate(() => import('@components_iqfu/public/cities_link_list/CitiesLinkList'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const MarketInfo = lazyHydrate(() => import('@components_iqfu/public/market_info/MarketInfo'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const ArticlesForBeginner = lazyHydrate(
  () => import('@components_iqfu/public/articles_for_beginner/ArticlesForBeginner'),
  {
    on: [
      ['scroll', () => document],
      ['delay', 500],
    ],
    compatibleMode: true,
  },
);

const UsersVoice = lazyHydrate(() => import('@components_iqfu/public/users_voice/UsersVoice'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const Footer = lazyHydrate(() => import('@components_iqfu/public/footer/Footer'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

export type CompaniesCitiesCommonProps = {
  isValidUrl: boolean;
  pageRenderParameter: CompaniesPageParameter;
  noindexFlag: boolean;
  serverCachedCompaniesData: { companies: CompanyListItem[]; companyMapBounds: CompanyMapBounds | null };
  serverCachedSaleCasesList: SaleCaseItem[];
  cityCode: string;
  cityInfo: CityInfo | null;
  majorCityWards: MajorCityWard[];
  marketData: MarketData | null;
  cityGroups: CityGroups | null;
  cityLatLong: { lat: number; lng: number };
  landSalesStats: SalesStatsData | null;
  houseSalesStats: SalesStatsData | null;
  mansionSalesStats: SalesStatsData | null;
  mediaContentArticles: MediaContentArticlesData | null;
};

export const CompaniesCitiesCommon: FC<CompaniesCitiesCommonProps> = ({
  isValidUrl,
  pageRenderParameter,
  noindexFlag,
  serverCachedCompaniesData,
  serverCachedSaleCasesList,
  cityCode,
  cityInfo,
  majorCityWards,
  marketData,
  cityGroups,
  cityLatLong,
  landSalesStats,
  houseSalesStats,
  mansionSalesStats,
  mediaContentArticles,
}) => {
  const router = useRouter();

  useEffect(() => {
    if (!isValidUrl) {
      router.replace('/404');
    }
  }, [isValidUrl]);

  const { data: companiesData } = useQuery(
    ['CompanyCityCompaniesList', cityCode, pageRenderParameter.companies_sort_query],
    async () => {
      const result = await getCompaniesList({
        city_code: Number(cityCode),
        limit: 100,
        sort: pageRenderParameter.companies_sort_query,
      });
      return {
        companies: result.companies ?? [],
        companyMapBounds: result.company_map_bounds,
      };
    },
    {
      enabled: isValidUrl && !!cityCode && !!pageRenderParameter.companies_sort_query,
      cacheTime: 1000 * 60 * 5,
      initialData: serverCachedCompaniesData,
    },
  );
  const companiesCount = companiesData?.companies.length ?? 0;

  const { data: saleCasesData } = useQuery(
    ['CompanyCitySaleCases', cityCode, pageRenderParameter.sale_cases_query],
    async () => {
      const result = await getSaleCasesList({
        city_code: Number(cityCode),
        property_type: pageRenderParameter.sale_cases_query,
        limit: 20,
      });
      return result.sale_cases || [];
    },
    {
      enabled: isValidUrl && !!cityCode,
      cacheTime: 1000 * 60 * 5,
      initialData: serverCachedSaleCasesList,
    },
  );

  const queryTypeString = pageRenderParameter.query_type_string;
  const pageQueryType = pageRenderParameter.page_query_type;
  const pageLabel = pageRenderParameter.page_label;
  const propertyTypeString = pageRenderParameter.property_type_string;
  const { height: windowHeight, width: windowWidth } = useWindowSize();
  const isMobile = useMediaQuery(MediaQuery.lg);

  //最初のタイトルの画面上端からのY座標を取得
  //※mobileでここまでスクロールしたらフローティングを表示する
  const [showFloatingThreshold, setShowFloatingThreshold] = useState(0);

  const setFloatingThresholdAfterRender = useCallback(
    async (node: HTMLHeadingElement) => {
      if (isMobile) {
        //同階層ページ遷移時に諸々のレンダリング完了してから取得しないと正しい値が取得できない
        await new Promise((resolve) => setTimeout(resolve, 100));
        setShowFloatingThreshold(node?.getBoundingClientRect().top + document.documentElement.scrollTop - windowHeight);
      }
    },
    [windowWidth, companiesData],
  );

  const pathParam = usePagePathParams();

  const { getCompaniesCityMetaTitle, getCompaniesCityMetaDescription, getCompaniesCityBreadCrumbsData } =
    getCompaniesPageMetaInfo;

  const leadSentence = (prefectureName: string, cityName: string, queryTypeString: string, pageQueryType: string) => {
    switch (pageQueryType) {
      case 'land':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: `${prefectureName}${cityName}での土地の売却実績をベースとしたおすすめ順にランキング形式で掲載しています。`,
        };
      case 'house':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: `${prefectureName}${cityName}での一戸建ての売却実績をベースとしたおすすめ順にランキング形式で掲載しています。`,
        };
      case 'mansion':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: `${prefectureName}${cityName}のマンションの売却実績をベースとしたおすすめ順にランキング形式で掲載しています。`,
        };
      case 'purchase':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: `${prefectureName}${cityName}をはじめとする買取実績をベースとしておすすめ順にランキング形式で掲載しています。`,
        };
      case 'divorce':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: '離婚による売却実績や豊富な経験を基におすすめ順にランキング形式で掲載しています。',
        };
      case 'inheritance':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: '相続による売却実績や豊富な経験を基におすすめ順にランキング形式で掲載しています。',
        };
      case 'leaseback':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: 'おすすめ順にランキング形式で掲載しています。',
        };
      case 'sell_high':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: 'おすすめ順にランキング形式で掲載しています。',
        };
      case 'sell_fast':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: 'おすすめ順にランキング形式で掲載しています。',
        };
      case 'any_properties':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: 'おすすめ順にランキング形式で掲載しています。',
        };
      case 'akiya':
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で空き家の売却が得意な地元の不動産会社を、`,
          second: `${prefectureName}${cityName}での空き家の売却実績や豊富な経験を基に、おすすめ順にランキング形式で掲載しています。`,
        };
      default:
        return {
          first: `「イクラ不動産　${prefectureName}${cityName}出張所」おすすめの地元で${queryTypeString}地元の不動産会社を、`,
          second: `${prefectureName}${cityName}での売却実績をベースとしたおすすめ順にランキング形式で掲載しています。`,
        };
    }
  };

  const metaTitle = getCompaniesCityMetaTitle(
    isValidUrl,
    cityInfo?.prefecture?.name,
    cityInfo?.city?.name,
    pageRenderParameter.query_type_string,
  );

  const metaDescription = getCompaniesCityMetaDescription(
    isValidUrl,
    cityInfo?.prefecture?.name,
    cityInfo?.city?.name,
    pageRenderParameter.property_type_string,
    pageRenderParameter.query_type_string,
  );

  const metaOGUrl = isValidUrl
    ? `${process.env.NEXT_PUBLIC_CLIENT_BASEPATH}/companies/cities/${cityCode}${
        pageRenderParameter.page_query_type ? '/' + pageRenderParameter.page_query_type : ''
      }`
    : '';

  return (
    <>
      {isValidUrl && (
        <PublicPageContainer>
          <Head>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDescription} key="description" />
            <meta property="og:title" content={metaTitle} key={'og_title'} />
            <meta property="og:description" content={metaDescription} key={'og_description'} />
            <meta property="og:url" content={metaOGUrl} key={'og_url'} />
            <meta name="twitter:title" content={metaTitle} key={'twitter_title'} />
            <meta name="twitter:description" content={metaDescription} key={'twitter_description'} />
            {noindexFlag && <meta name="robots" content="noindex" />}
            <link rel="canonical" href={metaOGUrl} />
          </Head>
          <BreadCrumbs
            breadCrumbs={getCompaniesCityBreadCrumbsData(
              cityInfo,
              pageRenderParameter.query_type_string,
              pageRenderParameter.page_query_type,
            )}
          />
          {cityInfo && (
            <>
              <div className={styles.page_container}>
                <div>
                  <div className={styles.top_title}>
                    {isValidUrl && (
                      <SectionLead isH1Tag>
                        【{showPrefectureNameInTitleFlag ? cityInfo.prefecture.name : ''}
                        {cityInfo.city.name}版】
                        <br className={styles.pc_only} />
                        {queryTypeString}地元の不動産屋さんランキング
                      </SectionLead>
                    )}
                  </div>

                  <PageMainLead>
                    {leadSentence(cityInfo.prefecture.name, cityInfo.city.name, queryTypeString, pageQueryType).first}
                    <br className="pc_only" />
                    {leadSentence(cityInfo.prefecture.name, cityInfo.city.name, queryTypeString, pageQueryType).second}
                  </PageMainLead>
                </div>

                {cityInfo.city_category === 'major' && cityGroups && (
                  <div className={styles.major_city_top_link_area}>
                    <div className={styles.major_city_wards_link_list_container}>
                      <MajorCityWardsLinkList
                        majorCityName={cityInfo.city.name}
                        majorCityWardList={majorCityWards}
                        pageType={'company'}
                        pageQueryType={pageQueryType}
                      />
                    </div>
                  </div>
                )}
                <div className={`${styles.top10_rank_area}`}>
                  <div className={styles.search_companies_sectionTitle_container}>
                    <IkukoSectionTitleWrapper>
                      <span className="title_text_strong">
                        {cityInfo.prefecture.name}
                        {cityInfo.city.name}
                      </span>
                      で{queryTypeString}
                      地元の不動産屋さんランキング
                    </IkukoSectionTitleWrapper>
                  </div>
                  {companiesData && (
                    <>
                      <CompaniesCitiesCompanyRanking
                        displayCompaniesData={companiesData.companies}
                        cityInfo={cityInfo}
                        pageRenderParameter={pageRenderParameter}
                        setFloatingThresholdAfterRender={setFloatingThresholdAfterRender}
                      />
                      {/* モバイルで会社ランキングが1社以上5社未満の場合、ここまでスクロールしたらフローティングを表示する */}
                      {isMobile && companiesCount > 0 && companiesCount < 5 ? (
                        <div ref={setFloatingThresholdAfterRender} />
                      ) : (
                        <></>
                      )}
                      {companiesCount > 0 && (
                        <>
                          <div className={styles.strength_based_company_ranking_links_container}>
                            <StrengthBasedCompanyRankingLinks
                              leadStyle={'blue'}
                              prefecture={{ code: String(cityInfo.prefecture.code), name: cityInfo.prefecture.name }}
                              city={{ code: String(cityInfo.city.code), name: cityInfo.city.name }}
                              location={'upper'}
                            />
                          </div>
                          <div className={styles.section_lead}>
                            <SectionLeadLeftAlign>近くの不動産屋さんに相談する！</SectionLeadLeftAlign>
                          </div>
                          <div className={styles.ranking_map_container}>
                            <RenderOnScroll dependency={pageRenderParameter}>
                              <CompanyLocationsMap
                                pageKey={`${pageLabel}${cityInfo.city.code}`}
                                mapType="rankCompaniesMap"
                                lat={cityLatLong.lat}
                                long={cityLatLong.lng}
                                zoom={11}
                                rankingCompaniesData={companiesData.companies}
                                mapBounds={companiesData.companyMapBounds}
                                fitBounds
                              />
                            </RenderOnScroll>
                          </div>
                        </>
                      )}

                      {companiesCount < 6 && (
                        <div className={styles.internal_links_beneath_near_companies}>
                          <InternalLinksCommon
                            cityInfo={cityInfo}
                            queryTypeString={queryTypeString}
                            pageQueryType={pageQueryType}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                <RankingShortColumn type={pageQueryType ? pageQueryType : 'default'} />

                <div className={styles.cta_container}>
                  <SecondaryCTA />
                </div>

                {/* モバイルで会社ランキングが0社の場合、ここまでスクロールしたらフローティングを表示する */}
                {isMobile && companiesCount === 0 ? <div ref={setFloatingThresholdAfterRender} /> : <></>}

                {mediaContentArticles && mediaContentArticles.media_content_articles?.length > 0 ? (
                  <div className={styles.media_content_articles_container}>
                    <div className={styles.media_content_articles_lead}>
                      <SectionLeadLeftAlign>
                        {cityInfo.city.name}で{pageRenderParameter.query_type_string}
                        不動産会社をお探しの方におすすめの記事
                      </SectionLeadLeftAlign>
                    </div>
                    <ul className={styles.media_content_articles_list}>
                      {mediaContentArticles.media_content_articles.map((article, i) => {
                        return (
                          <li className={styles.media_content_articles_item} key={i}>
                            <div>
                              <img src={`/images/iqfu/public/link_text_blue_circle.svg`} alt="" loading="lazy" />
                            </div>
                            <ExternalLink
                              href={`${article.media_url}?utm_source=iqfu&utm_medium=referral&utm_campaign=companies_cities_common&utm_content=${pathParam}`}
                              target={isMobile ? '_self' : '_blank'}
                            >
                              {article.title}
                            </ExternalLink>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <></>
                )}

                <div className={styles.search_companies_container}>
                  <SearchCompaniesWrapper hasShadow>
                    <PricePageLinks
                      pageLabel={pageLabel}
                      areaInfo={{
                        areaName: cityInfo.city.name,
                        areaType: 'city',
                        areaCode: cityInfo.city.code,
                      }}
                    />
                    <SearchLocalCompanies
                      defaultPrefecture={String(cityInfo.prefecture.code)}
                      defaultCity={String(cityInfo.city.code)}
                      pageLabel={pageLabel}
                      location={'upper'}
                      pageQueryType={pageQueryType}
                      queryTypeString={queryTypeString}
                    />
                    <StrengthBasedCompanyRankingLinks
                      leadStyle={'yellow'}
                      prefecture={{ code: String(cityInfo.prefecture.code), name: cityInfo.prefecture.name }}
                      city={{ code: String(cityInfo.city.code), name: cityInfo.city.name }}
                      location={'lower'}
                    />
                  </SearchCompaniesWrapper>
                </div>

                <div className={styles.check_price_container}>
                  <CheckPrice
                    pageLabel={pageLabel}
                    searchOption={{
                      area: 'prefecture',
                      prefecture: cityInfo.prefecture,
                    }}
                    cityCode={String(cityInfo.city.code)}
                  />
                </div>

                <div className={styles.articles_for_beginner_container}>
                  <ArticlesForBeginner pageLabel={pageLabel} />
                </div>

                <div className={styles.users_voice_container}>
                  <UsersVoice />
                </div>

                <div className={styles.market_container}>
                  <MarketInfo
                    marketDataTitle={`${cityInfo.address_summary}の不動産売却の「動向」`}
                    marketData={marketData}
                  />
                </div>

                {landSalesStats && houseSalesStats && mansionSalesStats ? (
                  <CompaniesPriceGeneralInfo
                    pageType={cityInfo.city_category === 'major' ? 'major_city' : 'city'}
                    areaInfo={{
                      prefecture: cityInfo.prefecture.name,
                      city: cityInfo.city.name,
                      parent_major_city: cityInfo.parent_major_city?.name,
                      city_category: cityInfo.city_category,
                    }}
                    landDisplayData={landSalesStats}
                    houseDisplayData={houseSalesStats}
                    mansionDisplayData={mansionSalesStats}
                  />
                ) : (
                  <></>
                )}

                {saleCasesData && saleCasesData.length > 0 && (
                  <div className={styles.sale_case_section}>
                    <div className={styles.saleCase_lead_container}>
                      <SectionLeadLeftAlign>
                        【{cityInfo.address_summary}】 {propertyTypeString ? `${propertyTypeString}の` : ''}
                        直近の売却実績一覧
                      </SectionLeadLeftAlign>
                    </div>
                    <SaleCaseArea
                      saleCasesData={saleCasesData}
                      showTableTitle={!isInCompanyPropertyTypePage(pageLabel)}
                      pageLabel={pageLabel}
                      onlyLandSaleCases={pageLabel.indexOf('land') !== -1}
                    />
                  </div>
                )}

                <div className={styles.map_area_container}>
                  <div className={styles.map_lead_wrap}>
                    <SectionLeadLeftAlign>イクラ不動産{cityInfo.address_summary}出張所地図</SectionLeadLeftAlign>
                  </div>
                  <div className={styles.map_container}>
                    <RenderOnScroll dependency={pageRenderParameter}>
                      <CompanyLocationsMap
                        mapType="onlyCenterCompanyMap"
                        lat={cityLatLong.lat}
                        long={cityLatLong.lng}
                        zoom={11}
                      />
                    </RenderOnScroll>
                  </div>
                </div>

                {cityGroups && (
                  <div className={styles.area_link_list_container}>
                    <div className={styles.section_lead}>
                      <SectionLeadLeftAlign>
                        「地域別」{queryTypeString === CompaniesPageCategory.default ? '売却に強い' : queryTypeString}
                        地元の不動産屋さんランキングを見る
                      </SectionLeadLeftAlign>
                    </div>
                    <CitiesLinkList
                      pageType="companies"
                      prefectureId={cityInfo.prefecture.code}
                      pageQueryType={pageQueryType}
                      citiesData={cityGroups}
                      location={'upper'}
                    />
                  </div>
                )}

                <div className={styles.articles_for_beginner_container}>
                  <ArticlesForBeginner pageLabel={pageLabel} />
                </div>

                <div className={`${styles.cta_container}`}>
                  <PrimaryCTA hasTopPageLink />
                </div>

                <div className={styles.page_bottom_link_container}>
                  <Link
                    href={
                      cityInfo.city_category === 'major_city_ward' && cityInfo.parent_major_city
                        ? `/companies/cities/${cityInfo.parent_major_city.code}/${pageQueryType}`
                        : `/companies/prefectures/${cityInfo.prefecture.code}/${pageQueryType}`
                    }
                    prefetch={false}
                    passHref
                    className={styles.page_bottom_link_button}
                  >
                    【
                    {cityInfo.city_category === 'major_city_ward' && cityInfo.parent_major_city?.name
                      ? `${cityInfo.parent_major_city?.name}`
                      : `${cityInfo.prefecture.name}`}
                    版】{queryTypeString}
                    <br className="pc_only" />
                    不動産屋さんランキング
                  </Link>
                </div>

                <div className={styles.prefectures_link_list_container}>
                  <div className={styles.section_lead}>
                    <SectionLeadLeftAlign>
                      「都道府県別」{queryTypeString === CompaniesPageCategory.default ? '売却に強い' : queryTypeString}
                      地元の不動産屋さんランキングを見る
                    </SectionLeadLeftAlign>
                  </div>
                  <PrefecturesLinkList category="company" pageQueryType={pageQueryType} location={'upper'} />
                </div>
              </div>

              <FloatingCV pageLabel={pageLabel} showFloatingThreshold={showFloatingThreshold} />

              <Footer />
            </>
          )}
        </PublicPageContainer>
      )}
    </>
  );
};

export default CompaniesCitiesCommon;
